import React from 'react';
import styled from 'styled-components';

import Gallery from '../gallery';
import Title from './title';
import Section from './section';
import Location from './location';
import NearBy from './nearby';
import NearByHomes from './nearby-homes';
import Benefits from './benefits';
import FeeDetails, { FeesAndPrice } from './fees-details';
import ContactUs from './contact-us';
import PropertyFields from './property-fields';
import WhatsAvailable from './whats-available';
import { MultiUnitFee } from './fees';
import { useMediaQuery } from 'react-responsive';
import MobileCtas from './mobile-ctas';
import Button from 'components/button';
import useAnchorTab from 'hooks/useAnchorTab';
import AvailableDates from './available-dates';

const Wrapper = styled.div`
  background-color: #f9f9f9;
  flex: 1;
  overflow-y: auto;
  padding-bottom: 60px;
`;

const Content = styled.div`
  padding: 20px;
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 10px;
  max-width: 1300px;
  margin: 0 auto;
  .feesSection,
  .contactUs,
  .mobileCTAS {
    display: none;
  }
  .contactUs {
    display: none;
  }
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    .rightPane {
      display: none;
    }
    .feesSection,
    .contactUs {
      display: block;
    }
    .propertyPrice {
      text-align: center;
      border-top: none;
    }
    .mobileCTAS {
      display: flex;
    }
  }
`;

const LeftPane = styled.div``;

const RightPane = styled.div`
  position: sticky;
  top: 50px;
  height: 100vh;
`;

const Tabs = styled.div`
  margin: 0px;
  position: sticky;
  background-color: #fff;
  top: -1px;
  left: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #e1e5eb;
  height: 45px;
  padding: 0 10px;
  gap: 20px;
  button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0px;
    margin: 0px;
    color: var(--color-font-primary);
    &.active {
      font-weight: bold;
    }
    @media (max-width: 480px) {
      font-size: 14px;
    }
  }
`;

export default ({
  orgId,
  location = {},
  nearbyLocations,
  nearbySchools,
  hideFloorplans,
  hideFees,
  hideUnits,
  isFullPage,
  floorplan
}) => {
  const galleryIsMobile = useMediaQuery({ maxWidth: 768 });

  const { activeTab, setActiveTab, onScrollEnd, scrollTop } = useAnchorTab({
    anchors: ['details', 'features', 'location', 'otherHomes'],
    container: 'scrollableDetails'
  });

  const tabs = [
    { label: 'Overview', id: 'details' },
    { label: 'Rental Features', id: 'features' },
    {
      label: 'Location',
      id: 'location',
      hidden: ({ address }) => !address?.geopoint?.[1] || !address?.geopoint?.[0]
    },
    {
      label: 'Other homes',
      id: 'otherHomes',
      hidden: ({ isFullPage, nearbyLocations }) => isFullPage || !nearbyLocations?.length
    }
  ];

  const tabProps = { address: location.address, isFullPage, nearbyLocations, hideFees };

  return (
    <Wrapper id="scrollableDetails" onScroll={onScrollEnd}>
      <div id="details" />
      {scrollTop > 400 && (
        <Tabs>
          {tabs
            .filter((tab) => !tab?.hidden?.(tabProps))
            .map((tab) => (
              <Button
                key={tab.id}
                className={tab.id === activeTab ? 'active' : ''}
                onClick={() => setActiveTab(tab.id)}>
                {tab.label}
              </Button>
            ))}
        </Tabs>
      )}
      <Gallery location={location} isSlider={galleryIsMobile} />
      <Content>
        <LeftPane>
          <Title location={location} />
          <AvailableDates location={location} floorplan={floorplan} />
          <Section className="feesSection">
            <FeesAndPrice location={location} />
          </Section>
          <Section label="RENTAL FEATURES" id="features">
            <PropertyFields location={location} />
          </Section>
          {!hideFloorplans && !!location?.floorplans?.length && (
            <Section label="WHAT'S AVAILABLE">
              <WhatsAvailable orgId={orgId} location={location} isFullPage={isFullPage} />
            </Section>
          )}
          {!hideUnits && !!location?.units?.length && (
            <Section label="WHAT'S AVAILABLE">
              <MultiUnitFee orgId={orgId} location={location} isFullPage={isFullPage} />
            </Section>
          )}
          <Location
            location={location}
            nearbySchools={nearbySchools}
            nearbyLocations={nearbyLocations}
          />
          <NearBy
            location={location}
            nearbySchools={nearbySchools}
            nearbyLocations={nearbyLocations}
          />
          <Benefits location={location} />
          <NearByHomes
            location={location}
            nearbySchools={nearbySchools}
            nearbyLocations={nearbyLocations}
          />
          <ContactUs location={location} className="contactUs" />
        </LeftPane>
        <RightPane className="rightPane">
          <FeeDetails location={location} hideFees={hideFees} floorplan={floorplan} />
          <ContactUs location={location} />
        </RightPane>
        <MobileCtas location={location} floorplan={floorplan} />
      </Content>
    </Wrapper>
  );
};
